import React from "react";
import { motion } from "framer-motion";
import { Phone, Instagram, MessageCircle, MapPin, Clock } from "lucide-react";
import { PHONE, PHONE_INTL, INSTAGRAM, buildWhatsAppLink } from "../data/menu";

export const Contact = () => {
  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[2.5rem] bg-[#3E2723] text-[#FFF0F3] p-8 md:p-14 lg:p-20 shadow-[0_40px_80px_rgba(62,39,35,0.35)]"
        >
          {/* Decorative blurred blobs */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#D38C41]/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-20 w-[480px] h-[480px] rounded-full bg-[#E76F7A]/25 blur-3xl" />

          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF0F3]/10 border border-[#FFF0F3]/20 px-4 py-2 text-xs font-bold tracking-widest uppercase mb-6">
                Pide ya
              </div>
              <h2
                className="font-display font-black leading-[0.95] tracking-tight"
                style={{ fontSize: "clamp(2.4rem, 6vw, 5.5rem)" }}
                data-testid="contact-heading"
              >
                ¿Antojado?{" "}
                <span className="italic text-[#F5B25E]">Te lo preparamos.</span>
              </h2>
              <p className="mt-5 text-[#FFF0F3]/80 text-base md:text-lg font-body max-w-md">
                Escríbenos por WhatsApp o llámanos. Síguenos en Instagram para
                ver los lanzamientos antes que nadie.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={buildWhatsAppLink()}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1fb957] text-white px-6 py-4 font-bold buffy-link shadow-[0_15px_35px_rgba(37,211,102,0.45)]"
                >
                  <MessageCircle size={18} />
                  WhatsApp
                </a>
                <a
                  href={`tel:+57${PHONE}`}
                  data-testid="contact-call-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FFF0F3] text-[#3E2723] px-6 py-4 font-bold buffy-link"
                >
                  <Phone size={18} />
                  Llamar
                </a>
                <a
                  href={`https://instagram.com/${INSTAGRAM}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-instagram-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-[#FFF0F3]/30 hover:bg-[#FFF0F3]/10 text-[#FFF0F3] px-6 py-4 font-bold buffy-link"
                >
                  <Instagram size={18} />@{INSTAGRAM}
                </a>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <InfoCard
                icon={Phone}
                label="Número"
                value={PHONE}
                href={`tel:+57${PHONE}`}
                testId="contact-info-phone"
              />
              <InfoCard
                icon={Instagram}
                label="Instagram"
                value={`@${INSTAGRAM}`}
                href={`https://instagram.com/${INSTAGRAM}`}
                testId="contact-info-instagram"
              />
              <InfoCard
                icon={Clock}
                label="Horario"
                value="Lun – Dom · 2 pm a 10 pm"
                testId="contact-info-hours"
              />
              <InfoCard
                icon={MapPin}
                label="Entregas"
                value="Domicilios por WhatsApp"
                testId="contact-info-delivery"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const InfoCard = ({ icon: Icon, label, value, href, testId }) => {
  const Cmp = href ? "a" : "div";
  const extra = href
    ? { href, target: href.startsWith("http") ? "_blank" : undefined, rel: "noreferrer" }
    : {};
  return (
    <Cmp
      {...extra}
      data-testid={testId}
      className="block rounded-[1.5rem] bg-[#FFF0F3]/10 backdrop-blur-sm border border-[#FFF0F3]/15 p-5 hover:bg-[#FFF0F3]/15 transition-colors duration-300"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-[#FFF0F3]/70">
        <Icon size={14} />
        {label}
      </div>
      <div className="mt-2 font-display font-extrabold text-lg md:text-xl">
        {value}
      </div>
    </Cmp>
  );
};

export default Contact;
